import {Position} from '@xyflow/react';
import dagre from 'dagre'
import vehicleService from "../../../../../services/web/vehicle-service.js";
import React from "react";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

export function getEdgeParams(source, target) {
    const sourceIntersectionPoint = getNodeIntersection(source, target);
    const targetIntersectionPoint = getNodeIntersection(target, source);

    const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
    const targetPos = getEdgePosition(target, targetIntersectionPoint);

    return {
        sx: sourceIntersectionPoint.x,
        sy: sourceIntersectionPoint.y,
        tx: targetIntersectionPoint.x,
        ty: targetIntersectionPoint.y,
        sourcePos,
        targetPos,
    }
}

export function getLayoutedElements({
        fixedNode,
        newNodes,
        newEdges,
        existingNodes = [],
        layoutType = "dagre",
        direction = "LR",
        nodeWidth = 150,
        nodeHeight = 150,
    }) {

    if (layoutType === "circle") {
        if (!newNodes.length) return [];

        const coincidentNode = newNodes.find(
            (n) => n.type === "coincidentPassages"
        );

        const circleNodes = newNodes.filter(
            (n) => n.type !== "coincidentPassages"
        );

        const fixedPos = fixedNode.position;
        const fixedWidth = fixedNode.measured.width;
        const fixedHeight = fixedNode.measured.height;

        const spacing = 80;

        let positionedCoincident = null;

        if (coincidentNode) {
            positionedCoincident = {
                ...coincidentNode,
                position: {
                    x: fixedPos.x + fixedWidth + spacing,
                    y: fixedPos.y + fixedHeight / 2,
                },
                sourcePosition: "right",
                targetPosition: "left",
            };
        }

        if (!circleNodes.length) {
            return positionedCoincident ? [positionedCoincident] : [];
        }

        const minSpacing = Math.max(nodeWidth, nodeHeight);
        const radius = (circleNodes.length * minSpacing) / (2 * Math.PI);

        const centerX =
            (positionedCoincident?.position.x ?? fixedPos.x) +
            nodeWidth +
            radius +
            spacing;

        const centerY = positionedCoincident?.position.y ?? fixedPos.y + fixedHeight / 2;

        const circular = layoutCircular({
            nodes: circleNodes,
            centerX,
            centerY,
            radius,
        });

        return positionedCoincident
            ? [positionedCoincident, ...circular]
            : circular;
    }

    return layoutDagre({
        newNodes,
        newEdges,
        existingNodes,
        direction,
        nodeWidth,
        nodeHeight,
    });
}

function layoutCircular({nodes, centerX, centerY, radius = 300}) {
    const angleStep = (2 * Math.PI) / nodes.length;

    return nodes.map((node, index) => {
        const angle = index * angleStep;

        return {
            ...node,
            position: {
                x: centerX + radius * Math.cos(angle),
                y: centerY + radius * Math.sin(angle),
            },
            sourcePosition: "right",
            targetPosition: "left",
        };
    });
}

function layoutDagre({
         newNodes,
         newEdges,
         existingNodes,
         direction = "LR",
         nodeWidth = 150,
         nodeHeight = 150,
     }) {
    const isHorizontal = direction === "LR";
    const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

    dagreGraph.setGraph({ rankdir: direction });

    newNodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    newEdges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const maxX =
        existingNodes.length > 0
            ? Math.max(...existingNodes.map((n) => n.position.x))
            : 0;

    const baseX = maxX + nodeWidth + 100;

    return newNodes.map((node) => {
        const dagreNode = dagreGraph.node(node.id);

        return {
            ...node,
            targetPosition: isHorizontal ? "left" : "top",
            sourcePosition: isHorizontal ? "right" : "bottom",
            position: {
                x: dagreNode.x - nodeWidth / 2 + baseX,
                y:
                    dagreNode.y -
                    nodeHeight / 2 -
                    dagreGraph.graph().height / 2,
            },
        };
    });
}

const getNodeIntersection = (intersectionNode, targetNode) => {
    const {width: intersectionNodeWidth, height: intersectionNodeHeight} = intersectionNode.measured;
    const intersectionNodePosition = intersectionNode.internals.positionAbsolute;
    const targetPosition = targetNode.internals.positionAbsolute;

    const w = intersectionNodeWidth / 2;
    const h = intersectionNodeHeight / 2;

    const x2 = intersectionNodePosition.x + w;
    const y2 = intersectionNodePosition.y + h;
    const x1 = targetPosition.x + targetNode.measured.width / 2;
    const y1 = targetPosition.y + targetNode.measured.height / 2;

    const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
    const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
    const a = 1 / (Math.abs(xx1) + Math.abs(yy1) || 1);
    const xx3 = a * xx1;
    const yy3 = a * yy1;
    const x = w * (xx3 + yy3) + x2;
    const y = h * (-xx3 + yy3) + y2;

    return {x, y}
}

const getEdgePosition = (node, intersectionPoint) => {
    const n = {...node.internals.positionAbsolute, ...node}
    const nx = Math.round(n.x);
    const ny = Math.round(n.y);
    const px = Math.round(intersectionPoint.x);
    const py = Math.round(intersectionPoint.y);

    if (px <= nx + 1) {
        return Position.Left;
    }
    if (px >= nx + n.measured.width - 1) {
        return Position.Right;
    }
    if (py <= ny + 1) {
        return Position.Top;
    }
    if (py >= n.y + n.measured.height - 1) {
        return Position.Bottom;
    }

    return Position.Top;
}

export const calculateAge = (date) => {
    if (!date) return null;

    const birth = new Date(date);
    const now = new Date();

    let age = now.getFullYear() - birth.getFullYear();

    const monthDiff = now.getMonth() - birth.getMonth();
    const dayDiff = now.getDate() - birth.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age--;

    return `${age} anos`;
};

export const getValue = (value, pluralText, singularText) => {
    return value ? value + " " + (value > 1 ? pluralText : singularText) : null
}

export const getBrandModels = (search, callback) => {
    if (search === '') {
        callback([]);
    }

    vehicleService.getBrandModels(search)
        .then((data) => {
            callback(data.map(({ id, text }) => ({ value: id, label: text })));
        })
}